<template>
    <div class="ds-segmented-picker" :class="[`ds-segmented-picker--align-${align}`, `ds-segmented-picker--${size}`]">
        <nav
            class="ds-segmented-picker__nav"
            :class="{ 'is-dark': theme === 'dark', 'is-full': isFullWidth }"
            ref="navEl"
        >
            <div class="ds-segmented-picker__slider" ref="pickerSlider"></div>
            <button
                class="ds-segmented-picker__toggle"
                :class="{
                    'is-active': index === activeIndex,
                    'is-dark': theme === 'dark',
                }"
                v-for="(title, index) in tabTitles"
                :key="title"
                @click="toggleClick(index)"
                ref="segmentButtons"
                :aria-label="title"
                :data-test="dataTestValue ? `${dataTestValue}-${normalizeText(title)}` : null"
                :data-testid="tabDataTestids ? tabDataTestids[index] : null"
            >
                <span class="ds-segmented-picker__text" :data-title="title">
                    <span
                        class="ds-segmented-picker__text-inner"
                        aria-hidden="true"
                        data-test="segmented-picker-text"
                        >{{ title }}</span
                    >
                </span>
            </button>
        </nav>
        <slot></slot>
    </div>
</template>

<script lang="ts" setup>
import { useSlots, ref, provide, watch, useTemplateRef, onMounted, onBeforeUnmount, computed } from "vue";
import type { Theme } from "../types/theme";
import { dsSegmentedInjectedKey } from "./ds-segmented-injected-key";
import { CustomEaseDurations, CustomEaseNames, useGsap } from "@sazka/utils/gsap-register";

const navEl = useTemplateRef("navEl");
const pickerSlider = useTemplateRef("pickerSlider");
const segmentButtons = ref<HTMLElement[]>([]);

defineExpose({
    navEl,
});

const props = withDefaults(
    defineProps<{
        /**
         * Set theme
         */
        theme?: Theme;
        /**
         * Set full width of picker
         */
        isFullWidth?: boolean;
        /**
         * Set alignment of picker
         */
        align?: "left" | "center" | "right";
        /**
         * Enable to use v-for inside slots
         */
        useFor?: boolean;
        /**
         * Size of picker
         */
        size?: "sm" | "lg";
        /**
         * Enables data-test attribute for SIT testing
         */
        dataTestValue?: string;
    }>(),
    {
        isFullWidth: false,
        align: "left",
        useFor: false,
        size: "sm",
        enabledDataTest: false,
    }
);

const modelValue = defineModel<string>({ required: false });

const emit = defineEmits(["toggleClick"]);

const slots = useSlots();

const tabTitles = computed<string[]>(() => {
    // https://stackoverflow.com/questions/70727383/using-v-for-inside-component-that-uses-slot
    // We need to override internal types from vue to make it work here
    return (
        props.useFor
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (slots.default()[0].children as any).map((tab) => tab.props?.title)
            : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              slots.default().map((tab) => tab.props?.title)
    ).filter(Boolean); // filter out empty titles (e.g. v-if on PickerItem)
});

const tabNames = computed<string[]>(() => {
    // https://stackoverflow.com/questions/70727383/using-v-for-inside-component-that-uses-slot
    return props.useFor
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (slots.default()[0].children as any).map((tab) => tab.props?.name)
        : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          slots.default().map((tab) => tab.props?.name);
});

const tabDataTestids = computed<string[]>(() => {
    // https://stackoverflow.com/questions/70727383/using-v-for-inside-component-that-uses-slot
    // We need to override internal types from vue to make it work here
    return (
        props.useFor
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (slots.default()[0].children as any).map((tab) => tab.props?.dataTestid)
            : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              slots.default().map((tab) => tab.props?.dataTestid)
    ).filter(Boolean); // filter out empty data-testids (e.g. v-if on PickerItem)
});

const activeIndex = ref(0);
const selectedTabKey = computed(() => tabNames.value[activeIndex.value]);

provide(dsSegmentedInjectedKey, selectedTabKey);

let buttonWidthsCache: number[] = [];
const calculateWidths = (entries: ResizeObserverEntry[]) => {
    buttonWidthsCache = entries.map((entry) => entry.borderBoxSize[0].inlineSize);
};

const toggleClick = (index: number) => {
    activeIndex.value = index;
    emit("toggleClick");
};

const { gsap } = useGsap();
let sliderTweenAnimation: gsap.core.Tween = null;
const moveSegment = async (index: number, withoutAnimation = false) => {
    const currentButton = segmentButtons.value[index];
    if (!currentButton) {
        return;
    }

    const currentWidth = buttonWidthsCache[index];
    pickerSlider.value.style.width = `${currentWidth}px`;

    // Calculate total width using pre-calculated widths
    const totalWidth = buttonWidthsCache.slice(0, index).reduce((acc, width) => acc + width, 0);

    let duration = CustomEaseDurations.MOTION_DURATION_250;
    if (withoutAnimation) {
        duration = 0;
    }
    if (sliderTweenAnimation) {
        sliderTweenAnimation.kill();
    }

    sliderTweenAnimation = gsap.to(pickerSlider.value, {
        translateX: totalWidth,
        duration: duration,
        ease: CustomEaseNames.MOTION_EASE_OUT,
        onComplete: () => {
            sliderTweenAnimation = null;
        },
    });
};

if (modelValue?.value) {
    watch(
        modelValue,
        (newModelValue) => {
            if (newModelValue) {
                const currentIndex = tabNames.value.indexOf(newModelValue);
                if (currentIndex !== -1) {
                    activeIndex.value = currentIndex;
                    return;
                }
            } else {
                // Reset to the first available tab if modelValue is invalid
                activeIndex.value = 0;
            }
        },
        { immediate: true }
    );
}

const normalizeText = (text: string): string => {
    return text
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
};

let resizeObserver: ResizeObserver = null;
onMounted(async () => {
    // Resize observer will setup initial values
    resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        calculateWidths(entries);
        moveSegment(activeIndex.value, true);
    });

    segmentButtons.value.forEach((segmentButton) => resizeObserver.observe(segmentButton));

    watch(activeIndex, (newActiveIndex) => {
        moveSegment(newActiveIndex);
        modelValue.value = tabNames.value[newActiveIndex];
    });
});

onBeforeUnmount(() => {
    if (resizeObserver) resizeObserver.disconnect();
});
</script>
